'use client';

import { PlayIcon } from '@heroicons/react/24/solid';
import { useTranslations } from 'next-intl';
import React from 'react';

import { HeroIcon, Image, Typography } from '@/components/atoms';
import { useBreakpoint } from '@/lib/hooks/util/useBreakpoint';
import { cn } from '@/lib/utils/tailwind';

import { HeroBanner } from '../Hero';
import { CTASpotlightBannerProps } from './types';
import { LinkButton } from '../LinkButton';

export const CTASpotlightBanner = ({
  title,
  contentType,
  imageUrl,
  content,
  primaryButtonText,
  href,
  className,
}: CTASpotlightBannerProps) => {
  const t = useTranslations();
  const isDesktop = useBreakpoint('sm');
  const contentTypeTranslated =
    contentType !== undefined
      ? t(`CONTENT_TYPE_${contentType.toUpperCase()}`)
      : '';
  const chosenMedia = imageUrl.landscape
    ? isDesktop
      ? imageUrl.landscape.url
      : imageUrl.portrait.url
    : 'img';
  return (
    <HeroBanner
      as={'header'}
      media={
        <>
          <Image
            alt={title}
            src={chosenMedia}
            className="object-cover"
            priority
          />
          <div
            className="absolute inset-0 bg-gradient-hero pointer-events-none"
            aria-hidden
          />
        </>
      }
      className={cn(
        'relative aspect-[4/5] sm:aspect-[21/12]',
        'overflow-hidden',
        'rounded-(--CTA-radius-default)',
        'bg-(--hero-gradient) ',
        className,
      )}
    >
      <div className="[align-items:var(--CTA-content-alignment,stretch)] [text-align:var(--CTA-text-alignment)] flex flex-col w-auto sm:w-[28.5rem] z-10 px-(--widget-padding-horizontal) py-(--widget-padding-vertical)">
        {contentTypeTranslated && (
          <div>
            <Typography.Text
              variant="body"
              className={cn([
                'hero-text-display',
                'hero-box-decoration',
                'px-0.5 md:px-2',
                'py-1',
                'w-fit',
                'hero-text-style',
                'bg-(--hero-text-background)',
                'text-shadow-(--hero-text-shadow)',
                'text-(--hero-text-color)',
              ])}
            >
              {contentTypeTranslated}
            </Typography.Text>
          </div>
        )}

        {title && (
          <div>
            <Typography.Heading
              variant={'h4'}
              className={cn([
                'hero-text-display',
                'hero-box-decoration',
                'px-0.5 md:px-2',
                'py-1',
                'w-fit',
                'bg-(--hero-text-background)',
                'text-(--hero-text-color)',
                'text-shadow-(--hero-text-shadow)',
              ])}
            >
              {title}
            </Typography.Heading>
          </div>
        )}

        {content && (
          <div>
            <Typography.Text
              variant={'paragraph'}
              className={cn([
                'hero-text-display',
                'hero-box-decoration',
                'px-0.5 md:px-2',
                'py-1',
                'w-fit',
                'hero-text-style',
                'bg-(--hero-text-background)',
                'text-shadow-(--hero-text-shadow)',
                'text-(--hero-text-color)',
                'line-clamp-2!',
                'sm:line-clamp-3!',
                'md:line-clamp-none!',
              ])}
            >
              {content}
            </Typography.Text>
          </div>
        )}

        <div className="mt-(--horizontal-spacing-6)">
          <LinkButton href={href}>
            <HeroIcon icon={PlayIcon} />
            {primaryButtonText}
          </LinkButton>
        </div>
      </div>
    </HeroBanner>
  );
};
